/**
 * useSlotMachineV2 Hook
 *
 * React hook for managing slot machine state and API interactions
 * Inspired by onyxdevs/react-slot-machine hook patterns
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { SymbolId } from '../themes/types';

// =============================================================================
// Types
// =============================================================================

export interface PaylineWin {
  lineNumber: number;
  symbolId: SymbolId;
  matchCount: number;
  positions: Array<{ col: number; row: number }>;
  winAmount: number;
}

export interface WildExpansion {
  col: number;
  originalRow: number;
  expandedRows: number[];
}

export interface SpinResultV2 {
  grid: SymbolId[][];
  wins: PaylineWin[];
  totalWin: number;
  engagementMultiplier: number;
  freeSpinMultiplier: number;
  features: {
    wildExpansions: WildExpansion[];
    freeSpinsTriggered: { spins: number; multiplier: number } | null;
    bonusTriggered: boolean;
    scatterCount: number;
  };
  jackpotPrize?: {
    id: string;
    type: string;
    value: string;
    months?: number;
    payout?: number;
    row?: number | null;
  } | null;
  nearMiss?: NearMiss | null;
  newBalance: {
    standard: number;
    fresh: number;
    super: number;
    total: number;
  };
  freeSpinSession?: { spins: number } | null;
  bonusGameStarted?: boolean;
}

export interface FreeSpinResult extends SpinResultV2 {
  freeSpinsRemaining: number;
  extraSpinsWon: number;
  sessionComplete: boolean;
}

export interface BonusPickResult {
  pickedItem: {
    index: number;
    type: string;
    value: number;
    isCollect?: boolean;
  };
  picksRemaining: number;
  totalWon: number;
  isComplete: boolean;
  newBalance?: {
    standard: number;
    fresh: number;
    super: number;
    total: number;
  };
}

// Hold & Spin Bonus Types
export interface HoldAndSpinGridPosition {
  hasRing: boolean;
  value: number | null;
}

export interface HoldAndSpinState {
  grid: HoldAndSpinGridPosition[];
  spinsRemaining: number;
  totalValue: number;
}

export interface HoldAndSpinStartResult {
  sessionId: string;
  grid: HoldAndSpinGridPosition[];
  spinsRemaining: number;
  totalValue: number;
}

export interface HoldAndSpinSpinResult {
  grid: HoldAndSpinGridPosition[];
  spinsRemaining: number;
  totalValue: number;
  newRings: Array<{ position: number; value: number }>;
  isComplete: boolean;
  isGrandBonus: boolean;
  payout?: number;
  newBalance?: {
    standard: number;
    fresh: number;
    super: number;
    total: number;
  };
}

// Near-miss types
export type NearMissType = 'free_spins' | 'bonus' | 'jackpot';

export interface NearMiss {
  type: NearMissType;
  currentCount: number;
  neededCount: number;
  criticalReels: number[];
}

export interface SlotStatus {
  balance: {
    standard: number;
    fresh: number;
    super: number;
    total: number;
  };
  engagementMultiplier: number;
  canDailySpin: boolean;
  activeFreeSpins: {
    sessionId: string;
    spinsRemaining: number;
    multiplier: number;
  } | null;
  activeBonusGame: {
    sessionId: string;
    picksRemaining: number;
  } | null;
}

export interface SlotConfig {
  config: {
    rows: number;
    columns: number;
    paylines: number;
    min_bet_per_line: number;
    max_bet_per_line: number;
    min_lines: number;
    max_lines: number;
  };
  paylines: Array<{
    line_number: number;
    positions: number[];
  }>;
  symbols: Array<{
    id: SymbolId;
    paytable: Record<string, number>;
    isWild?: boolean;
    isScatter?: boolean;
    isBonus?: boolean;
  }>;
}

export interface UseSlotMachineV2Options {
  /** API base URL */
  apiBaseUrl?: string;
  /** Authentication token getter */
  getAuthToken: () => string | null;
  /** Callback when spin completes */
  onSpinComplete?: (result: SpinResultV2) => void;
  /** Callback when a win occurs */
  onWin?: (result: SpinResultV2) => void;
  /** Callback for big wins */
  onBigWin?: (result: SpinResultV2) => void;
  /** Callback when free spins are triggered */
  onFreeSpinsTriggered?: (spins: number, multiplier: number) => void;
  /** Callback when bonus game is triggered */
  onBonusTriggered?: () => void;
  /** Callback when Hold & Spin bonus completes */
  onHoldAndSpinComplete?: (payout: number, isGrandBonus: boolean) => void;
  /** Callback when jackpot is won */
  onJackpot?: (prize: { id: string; type: string; value: string }) => void;
  /** Big win threshold (total win >= betAmount * threshold) */
  bigWinThreshold?: number;
}

export interface UseSlotMachineV2Return {
  // State
  isLoading: boolean;
  isSpinning: boolean;
  error: string | null;
  status: SlotStatus | null;
  config: SlotConfig | null;
  lastResult: SpinResultV2 | null;

  // Betting state
  betPerLine: number;
  activeLines: number;
  totalBet: number;

  // Feature states
  isFreeSpinMode: boolean;
  freeSpinsRemaining: number;
  freeSpinMultiplier: number;
  isBonusMode: boolean;
  bonusPicksRemaining: number;
  bonusItems: Array<{ revealed: boolean; value?: number; type?: string }>;

  // Hold & Spin state
  isHoldAndSpinMode: boolean;
  holdAndSpinState: HoldAndSpinState | null;

  // Actions
  setBetPerLine: (bet: number) => void;
  setActiveLines: (lines: number) => void;
  fetchStatus: () => Promise<void>;
  fetchConfig: () => Promise<void>;
  spin: (betType: 'free_daily' | 'paid') => Promise<SpinResultV2 | null>;
  spinFreeSpin: () => Promise<FreeSpinResult | null>;
  pickBonusItem: (index: number) => Promise<BonusPickResult | null>;
  startHoldAndSpin: () => Promise<HoldAndSpinStartResult | null>;
  spinHoldAndSpin: () => Promise<HoldAndSpinSpinResult | null>;
  closeHoldAndSpin: () => void;
  clearError: () => void;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useSlotMachineV2(options: UseSlotMachineV2Options): UseSlotMachineV2Return {
  const {
    apiBaseUrl = '/api/spin-v2',
    getAuthToken,
    onSpinComplete,
    onWin,
    onBigWin,
    onFreeSpinsTriggered,
    onBonusTriggered,
    onHoldAndSpinComplete,
    onJackpot,
    bigWinThreshold = 10,
  } = options;

  // Core state
  const [isLoading, setIsLoading] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SlotStatus | null>(null);
  const [config, setConfig] = useState<SlotConfig | null>(null);
  const [lastResult, setLastResult] = useState<SpinResultV2 | null>(null);

  // Betting state
  const [betPerLine, setBetPerLine] = useState(1);
  const [activeLines, setActiveLines] = useState(20);

  // Free spin state
  const [isFreeSpinMode, setIsFreeSpinMode] = useState(false);
  const [freeSpinsRemaining, setFreeSpinsRemaining] = useState(0);
  const [freeSpinMultiplier, setFreeSpinMultiplier] = useState(1);

  // Bonus game state
  const [isBonusMode, setIsBonusMode] = useState(false);
  const [bonusPicksRemaining, setBonusPicksRemaining] = useState(0);
  const [bonusItems, setBonusItems] = useState<
    Array<{ revealed: boolean; value?: number; type?: string }>
  >([]);

  // Hold & Spin state
  const [isHoldAndSpinMode, setIsHoldAndSpinMode] = useState(false);
  const [holdAndSpinState, setHoldAndSpinState] = useState<HoldAndSpinState | null>(null);

  // Prevent double-spin
  const spinLockRef = useRef(false);

  const totalBet = betPerLine * activeLines;

  // API helper
  const apiCall = useCallback(
    async <T>(
      endpoint: string,
      method: 'GET' | 'POST' = 'GET',
      body?: unknown
    ): Promise<T | null> => {
      const token = getAuthToken();
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      try {
        const response = await fetch(`${apiBaseUrl}${endpoint}`, {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: body ? JSON.stringify(body) : undefined,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || `API error: ${response.status}`);
        }

        return data.data as T;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        return null;
      }
    },
    [apiBaseUrl, getAuthToken]
  );

  // Fetch slot machine status
  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    const data = await apiCall<SlotStatus>('/status');
    if (data) {
      setStatus(data);

      // Update free spin mode
      if (data.activeFreeSpins) {
        setIsFreeSpinMode(true);
        setFreeSpinsRemaining(data.activeFreeSpins.spinsRemaining);
        setFreeSpinMultiplier(data.activeFreeSpins.multiplier);
      } else {
        setIsFreeSpinMode(false);
        setFreeSpinsRemaining(0);
        setFreeSpinMultiplier(1);
      }

      // Update bonus mode
      if (data.activeBonusGame) {
        setIsBonusMode(true);
        setBonusPicksRemaining(data.activeBonusGame.picksRemaining);
        // Initialize bonus items (12 items, all hidden)
        setBonusItems(Array(12).fill({ revealed: false }));
      } else {
        setIsBonusMode(false);
        setBonusPicksRemaining(0);
        setBonusItems([]);
      }
    }
    setIsLoading(false);
  }, [apiCall]);

  // Fetch slot machine configuration
  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    const data = await apiCall<SlotConfig>('/config');
    if (data) {
      setConfig(data);
    }
    setIsLoading(false);
  }, [apiCall]);

  // Execute a spin
  const spin = useCallback(
    async (betType: 'free_daily' | 'paid'): Promise<SpinResultV2 | null> => {
      // Prevent double-spin
      if (spinLockRef.current || isSpinning) {
        return null;
      }

      // Check if we're in free spin or bonus mode
      if (isFreeSpinMode) {
        setError('Use spinFreeSpin() during free spin mode');
        return null;
      }
      if (isBonusMode) {
        setError('Complete bonus game before spinning');
        return null;
      }

      spinLockRef.current = true;
      setIsSpinning(true);
      setError(null);

      try {
        const result = await apiCall<SpinResultV2>('/execute', 'POST', {
          betType,
          betPerLine,
          activeLines,
        });

        if (result) {
          setLastResult(result);

          // Update status with new balance
          setStatus((prev) =>
            prev
              ? {
                  ...prev,
                  balance: result.newBalance,
                  canDailySpin: betType === 'paid' ? prev.canDailySpin : false,
                }
              : null
          );

          // Handle callbacks
          onSpinComplete?.(result);

          if (result.totalWin > 0) {
            onWin?.(result);

            // Check for big win
            if (result.totalWin >= totalBet * bigWinThreshold) {
              onBigWin?.(result);
            }
          }

          // Handle jackpot
          if (result.jackpotPrize) {
            onJackpot?.(result.jackpotPrize);
          }

          // Handle free spins triggered
          if (result.features.freeSpinsTriggered) {
            const { spins, multiplier } = result.features.freeSpinsTriggered;
            setIsFreeSpinMode(true);
            setFreeSpinsRemaining(spins);
            setFreeSpinMultiplier(multiplier);
            onFreeSpinsTriggered?.(spins, multiplier);
          }

          // Handle bonus triggered (Hold & Spin mode)
          if (result.bonusGameStarted) {
            setIsHoldAndSpinMode(true);
            // Initialize with empty grid - startHoldAndSpin will populate it
            setHoldAndSpinState({
              grid: Array(15).fill({ hasRing: false, value: null }),
              spinsRemaining: 3,
              totalValue: 0,
            });
            onBonusTriggered?.();
          }
        }

        return result;
      } finally {
        setIsSpinning(false);
        spinLockRef.current = false;
      }
    },
    [
      isSpinning,
      isFreeSpinMode,
      isBonusMode,
      apiCall,
      betPerLine,
      activeLines,
      totalBet,
      bigWinThreshold,
      onSpinComplete,
      onWin,
      onBigWin,
      onJackpot,
      onFreeSpinsTriggered,
      onBonusTriggered,
    ]
  );

  // Execute a free spin
  const spinFreeSpin = useCallback(async (): Promise<FreeSpinResult | null> => {
    if (spinLockRef.current || isSpinning) {
      return null;
    }

    if (!isFreeSpinMode) {
      setError('Not in free spin mode');
      return null;
    }

    spinLockRef.current = true;
    setIsSpinning(true);
    setError(null);

    try {
      const result = await apiCall<FreeSpinResult>('/free-spins/spin', 'POST', {
        betPerLine,
        activeLines,
      });

      if (result) {
        setLastResult(result);

        // Update free spin state
        setFreeSpinsRemaining(result.freeSpinsRemaining);

        // Update balance
        setStatus((prev) =>
          prev
            ? {
                ...prev,
                balance: result.newBalance,
              }
            : null
        );

        // Handle callbacks
        onSpinComplete?.(result);

        if (result.totalWin > 0) {
          onWin?.(result);
        }

        // Handle re-trigger
        if (result.extraSpinsWon > 0) {
          onFreeSpinsTriggered?.(result.extraSpinsWon, freeSpinMultiplier);
        }

        // End free spin mode if complete
        if (result.sessionComplete) {
          setIsFreeSpinMode(false);
          setFreeSpinsRemaining(0);
          setFreeSpinMultiplier(1);
        }
      }

      return result;
    } finally {
      setIsSpinning(false);
      spinLockRef.current = false;
    }
  }, [
    isSpinning,
    isFreeSpinMode,
    apiCall,
    betPerLine,
    activeLines,
    freeSpinMultiplier,
    onSpinComplete,
    onWin,
    onFreeSpinsTriggered,
  ]);

  // Pick a bonus item
  const pickBonusItem = useCallback(
    async (index: number): Promise<BonusPickResult | null> => {
      if (!isBonusMode) {
        setError('Not in bonus mode');
        return null;
      }

      setError(null);

      const result = await apiCall<BonusPickResult>('/bonus/pick', 'POST', {
        itemIndex: index,
      });

      if (result) {
        // Update revealed items
        setBonusItems((prev) => {
          const updated = [...prev];
          updated[index] = {
            revealed: true,
            value: result.pickedItem.value,
            type: result.pickedItem.type,
          };
          return updated;
        });

        // Update picks remaining
        setBonusPicksRemaining(result.picksRemaining);

        // End bonus mode if complete
        if (result.isComplete) {
          setIsBonusMode(false);
          setBonusPicksRemaining(0);

          // Update balance
          if (result.newBalance) {
            setStatus((prev) =>
              prev
                ? {
                    ...prev,
                    balance: result.newBalance!,
                  }
                : null
            );
          }
        }
      }

      return result;
    },
    [isBonusMode, apiCall]
  );

  // Start Hold & Spin bonus game
  const startHoldAndSpin = useCallback(async (): Promise<HoldAndSpinStartResult | null> => {
    if (!isHoldAndSpinMode) {
      setError('Bonus game not triggered');
      return null;
    }

    setError(null);

    const result = await apiCall<HoldAndSpinStartResult>('/bonus/start', 'POST', {
      betPerLine,
      activeLines,
    });

    if (result) {
      setHoldAndSpinState({
        grid: result.grid,
        spinsRemaining: result.spinsRemaining,
        totalValue: result.totalValue,
      });
    }

    return result;
  }, [isHoldAndSpinMode, apiCall, betPerLine, activeLines]);

  // Execute a Hold & Spin round
  const spinHoldAndSpin = useCallback(async (): Promise<HoldAndSpinSpinResult | null> => {
    if (!isHoldAndSpinMode || !holdAndSpinState) {
      setError('Not in Hold & Spin mode');
      return null;
    }

    setError(null);

    const result = await apiCall<HoldAndSpinSpinResult>('/bonus/spin', 'POST', {});

    if (result) {
      // Update Hold & Spin state
      setHoldAndSpinState({
        grid: result.grid,
        spinsRemaining: result.spinsRemaining,
        totalValue: result.totalValue,
      });

      // Handle completion
      if (result.isComplete) {
        // Update balance
        if (result.newBalance) {
          setStatus((prev) =>
            prev
              ? {
                  ...prev,
                  balance: result.newBalance!,
                }
              : null
          );
        }

        // Callback
        onHoldAndSpinComplete?.(result.payout || result.totalValue, result.isGrandBonus);
      }
    }

    return result;
  }, [isHoldAndSpinMode, holdAndSpinState, apiCall, onHoldAndSpinComplete]);

  // Close Hold & Spin mode (after celebration)
  const closeHoldAndSpin = useCallback(() => {
    setIsHoldAndSpinMode(false);
    setHoldAndSpinState(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch initial data on mount
  useEffect(() => {
    fetchStatus();
    fetchConfig();
  }, [fetchStatus, fetchConfig]);

  return {
    // State
    isLoading,
    isSpinning,
    error,
    status,
    config,
    lastResult,

    // Betting state
    betPerLine,
    activeLines,
    totalBet,

    // Feature states
    isFreeSpinMode,
    freeSpinsRemaining,
    freeSpinMultiplier,
    isBonusMode,
    bonusPicksRemaining,
    bonusItems,

    // Hold & Spin state
    isHoldAndSpinMode,
    holdAndSpinState,

    // Actions
    setBetPerLine: (bet: number) =>
      setBetPerLine(Math.min(Math.max(bet, 1), config?.config.max_bet_per_line ?? 10)),
    setActiveLines: (lines: number) =>
      setActiveLines(Math.min(Math.max(lines, 1), config?.config.max_lines ?? 20)),
    fetchStatus,
    fetchConfig,
    spin,
    spinFreeSpin,
    pickBonusItem,
    startHoldAndSpin,
    spinHoldAndSpin,
    closeHoldAndSpin,
    clearError,
  };
}

export default useSlotMachineV2;
